import { createContext, ReactNode, useContext, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import useLocalStorage from '@/hooks/useLocalStorage'
import { getBaseGrid, getNonGridFields, sortBySortOrder } from '@/utils/field'
import { focusOnField } from '@/utils/row-values'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { FieldInfo } from '@/components/workflow-state-page/SetStateFieldsDialog'
import { useDocumentRowsContext } from './DocumentRowsProvider'
import { useDocumentRowValuesContext } from './DocumentRowValuesProvider'
import { useSelectedFieldContext } from './SelectedFieldProvider'

type ExcludedFieldsForFavoritesProviderValue = {
  allFieldIds: string[]
  fieldsInfo: FieldInfo[]
  excludedFieldIds: string[]
  setExcludedFieldIds: (ids: string[]) => void
}

const ExcludedFieldsForFavoritesContext =
  createContext<ExcludedFieldsForFavoritesProviderValue>(
    {} as ExcludedFieldsForFavoritesProviderValue,
  )

export const useExcludedFieldsForFavoritesContext = () =>
  useContext(ExcludedFieldsForFavoritesContext)

type ExcludedFieldsForFavoritesProviderProps = {
  children: ReactNode
  isDisabled: boolean
}

export default function ExcludedFieldsForFavoritesProvider({
  children,
  isDisabled,
}: ExcludedFieldsForFavoritesProviderProps) {
  const { project } = useProjectContext()
  const { baseRow } = useDocumentRowsContext()
  const { getFieldDocumentRowValue } = useDocumentRowValuesContext()
  const { selectedField } = useSelectedFieldContext() || {}

  const onlyBaseFields = useMemo(
    () =>
      sortBySortOrder(
        getNonGridFields(
          getBaseGrid(project.project_grids || [])?.project_grid_fields || [],
        ),
      ) || [],
    [project.project_grids],
  )
  const fieldsInfo = useMemo(
    () =>
      onlyBaseFields.map((field) => ({
        field,
      })),
    [onlyBaseFields],
  )
  const allFieldIds = useMemo(
    () => onlyBaseFields.map((field) => field.id),
    [onlyBaseFields],
  )

  const [excludedFieldIds, setExcludedFieldIds] = useLocalStorage<string[]>(
    `${project.id}-validation-excluded-fields-ids-for-favorites`,
    allFieldIds,
  )

  const includedFieldIds = useMemo(
    () => allFieldIds.filter((id) => !excludedFieldIds.includes(id)),
    [allFieldIds, excludedFieldIds],
  )

  useHotkeys(
    'ctrl+meta+tab',
    () => {
      if (!baseRow) return
      let indexToFocus = 0
      if (selectedField) {
        const includedFieldIndex = includedFieldIds.indexOf(
          selectedField.field.id,
        )
        indexToFocus =
          includedFieldIndex < includedFieldIds.length - 1
            ? includedFieldIndex + 1
            : 0
      }
      const nextFieldId = includedFieldIds[indexToFocus]
      const rowValue = getFieldDocumentRowValue(nextFieldId, baseRow.id)

      if (rowValue) {
        focusOnField(rowValue.id)
      }
    },
    {
      enabled: !isDisabled,
      preventDefault: true,
      enableOnFormTags: true,
    },
    [selectedField, includedFieldIds],
  )

  return (
    <ExcludedFieldsForFavoritesContext.Provider
      value={{
        allFieldIds,
        fieldsInfo,
        excludedFieldIds,
        setExcludedFieldIds,
      }}
    >
      {children}
    </ExcludedFieldsForFavoritesContext.Provider>
  )
}
